import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
  type Placement,
} from '@floating-ui/react';
import { cloneElement, isValidElement, useState, type ReactElement, type ReactNode, type Ref } from 'react';
import { mergeRefs, usePrefersReducedMotion } from '@/shared/lib/hooks';
import s from './Tooltip.module.css';

export interface TooltipProps {
  content: ReactNode;
  /** Один элемент-триггер, принимающий ref и обработчики фокуса/наведения. */
  children: ReactElement;
  placement?: Placement;
  /** Задержка появления, мс. */
  delay?: number;
  disabled?: boolean;
  /**
   * `description` — подсказка дополняет элемент (aria-describedby).
   * `label` — подсказка повторяет доступное имя (aria-label у IconButton): экранный диктор её не читает дважды.
   */
  kind?: 'description' | 'label';
}

/** Всплывающая подсказка при наведении и фокусе с клавиатуры; Esc закрывает. */
export function Tooltip({ content, children, placement = 'top', delay = 300, disabled = false, kind = 'description' }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const { refs, floatingStyles, context } = useFloating({
    open: open && !disabled,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const hover = useHover(context, { delay: { open: delay, close: 0 }, move: false, enabled: !disabled });
  const focus = useFocus(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip', enabled: kind === 'description' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : 110,
    initial: { opacity: 0 },
  });

  if (!isValidElement(children)) return children;
  const child = children as ReactElement<Record<string, unknown>> & { ref?: Ref<unknown> };
  const reference = cloneElement(
    child,
    getReferenceProps({
      ...child.props,
      ref: mergeRefs(refs.setReference, child.ref as Ref<Element> | undefined),
    }),
  );

  return (
    <>
      {reference}
      {isMounted && content ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className={s.tooltip}
            style={{ ...floatingStyles, ...styles }}
            aria-hidden={kind === 'label' ? true : undefined}
            {...getFloatingProps()}
          >
            {content}
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}
